// src/pages/KarigarManagement/Job/JobDetailPage.jsx
// ============================================================================
// JOB DETAIL PAGE - FULLY RESPONSIVE VERSION
// ============================================================================
// Responsive Design Features:
// - Mobile-first approach with progressive enhancement
// - Optimized touch targets (min 44px) for mobile
// - Collapsible sections to save screen space on mobile
// - Sticky header with essential actions
// - Bottom sheet for mobile actions menu
// - Side sheet for mobile sidebar content
// - Scrollable horizontal tabs for mobile
// - Responsive grid layouts (1 col mobile, 2 col tablet, 3 col desktop)
// - Proper spacing scaling (tighter on mobile, looser on desktop)
// ============================================================================

import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useJobManagement, usePaymentManagement } from "@/hooks/useKarigar";
import { Button } from "@/components/ui-v2/button";
import { Input } from "@/components/ui-v2/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import { Badge } from "@/components/ui-v2/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui-v2/tabs";
import { Progress } from "@/components/ui-v2/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui-v2/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui-v2/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui-v2/sheet";
import { Textarea } from "@/components/ui-v2/textarea";
import { Label } from "@/components/ui-v2/label";
import { ScrollArea } from "@/components/ui-v2/scroll-area";
import {
  ArrowLeft,
  User,
  CheckCircle,
  AlertTriangle,
  Package,
  Calendar,
  RefreshCw,
  Star,
  UserPlus,
  Phone,
  Truck,
  ClipboardCheck,
  XCircle,
  MessageSquare,
  Send,
  Clock,
  Edit,
  Ban,
  Wrench,
  Image as ImageIcon,
  Upload,
  Loader2,
  Eye,
  Trash2,
  Camera,
  ShoppingCart,
  FileText,
  MoreVertical,
  ChevronDown,
  ChevronUp,
  X,
  Info,
} from "lucide-react";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import ImageUpload from "@/components/common/ImageUpload";
import { toast } from "sonner";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import JobMaterialsTab from "./Tabs/JobMaterialsTab";
import JobPaymentsTab from "./Tabs/JobPaymentsTab";

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_COLORS = {
  PENDING: "bg-yellow-100 text-yellow-800 border-yellow-200",
  ASSIGNED: "bg-blue-100 text-blue-800 border-blue-200",
  ACCEPTED: "bg-indigo-100 text-indigo-800 border-indigo-200",
  IN_PROGRESS: "bg-purple-100 text-purple-800 border-purple-200",
  ON_HOLD: "bg-gray-100 text-gray-800 border-gray-200",
  QUALITY_CHECK: "bg-orange-200 text-orange-900 border-orange-300",
  REVISION_REQUIRED: "bg-amber-100 text-amber-800 border-amber-200",
  COMPLETED: "bg-green-200 text-green-900 border-green-300",
  DELIVERED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  CANCELLED: "bg-red-100 text-red-800 border-red-200",
  REJECTED: "bg-red-100 text-red-800 border-red-200",
};

const PRIORITY_COLORS = {
  LOW: "bg-gray-100 text-gray-800",
  NORMAL: "bg-blue-100 text-blue-800",
  HIGH: "bg-orange-100 text-orange-800",
  URGENT: "bg-red-100 text-red-800",
  CRITICAL: "bg-red-200 text-red-900",
};

const QUALITY_RATING_STARS = {
  EXCELLENT: 5,
  GOOD: 4,
  SATISFACTORY: 3,
  NEEDS_IMPROVEMENT: 2,
  POOR: 1,
};

const RATING_NUMBER_TO_LABEL = {
  5: "EXCELLENT",
  4: "GOOD",
  3: "SATISFACTORY",
  2: "NEEDS_IMPROVEMENT",
  1: "POOR",
};

// ============================================================================
// RESPONSIVE IMAGE GALLERY COMPONENT
// ============================================================================

const ImageGallery = ({
  title,
  description,
  images = [],
  onUpload,
  onDelete,
  canUpload = true,
  canDelete = true,
  isUploading = false,
  maxFiles = 10,
  allowCamera = true,
}) => {
  const [newFiles, setNewFiles] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);

  const handleUpload = async () => {
    if (newFiles.length === 0) return;
    try {
      await onUpload(newFiles);
      setNewFiles([]);
      toast.success(`${newFiles.length} image(s) uploaded`);
    } catch {
      toast.error("Failed to upload images");
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader
        className="pb-3 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <CardTitle className="text-sm sm:text-base truncate">
                {title}
              </CardTitle>
              {description && (
                <CardDescription className="text-xs mt-0.5 hidden sm:block">
                  {description}
                </CardDescription>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs shrink-0">
              {images.length}
            </Badge>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-4 pt-0">
          {images.length > 0 && (
            <div className="grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-4 xl:grid-cols-5 gap-2">
              {images.map((img, idx) => (
                <div
                  key={idx}
                  className="relative group aspect-square rounded-lg overflow-hidden border bg-muted/50 cursor-pointer"
                  onClick={() => setPreviewImage(img)}
                >
                  <img
                    src={img.url || img}
                    alt={`Image ${idx + 1}`}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    <Button
                      type="button"
                      variant="secondary"
                      size="icon"
                      className="h-7 w-7 sm:h-8 sm:w-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewImage(img);
                      }}
                    >
                      <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                    {canDelete && onDelete && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="h-7 w-7 sm:h-8 sm:w-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(img.url || img);
                        }}
                      >
                        <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {canUpload && (
            <div className="space-y-3">
              <ImageUpload
                files={newFiles}
                onFilesChange={setNewFiles}
                maxFiles={maxFiles - images.length}
                maxSizeMB={5}
                allowCamera={allowCamera}
                compact
                gridCols={4}
                label=""
              />
              {newFiles.length > 0 && (
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setNewFiles([])}
                    disabled={isUploading}
                    className="h-9"
                  >
                    Clear
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleUpload}
                    disabled={isUploading}
                    className="h-9"
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        <span className="hidden xs:inline">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload {newFiles.length}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {images.length === 0 && !canUpload && (
            <div className="text-center py-6 text-muted-foreground">
              <ImageIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No images</p>
            </div>
          )}

          <Dialog
            open={!!previewImage}
            onOpenChange={() => setPreviewImage(null)}
          >
            <DialogContent className="max-w-[95vw] sm:max-w-3xl p-2 sm:p-6">
              <DialogHeader className="sr-only">
                <DialogTitle>Image Preview</DialogTitle>
              </DialogHeader>
              <div className="flex items-center justify-center min-h-[50vh]">
                <img
                  src={previewImage?.url || previewImage}
                  alt="Preview"
                  className="max-h-[80vh] max-w-full object-contain rounded-lg"
                />
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      )}
    </Card>
  );
};

// ============================================================================
// COLLAPSIBLE INFO SECTION
// ============================================================================

const CollapsibleSection = ({
  title,
  icon: Icon,
  children,
  defaultOpen = true,
  badge,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <Card className="overflow-hidden">
      <CardHeader
        className="py-3 px-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
            <CardTitle className="text-sm sm:text-base">{title}</CardTitle>
            {badge}
          </div>
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </CardHeader>
      {isOpen && <CardContent className="pt-0">{children}</CardContent>}
    </Card>
  );
};

// ============================================================================
// MOBILE ACTIONS SHEET
// ============================================================================

const MobileActionsSheet = ({
  job,
  canBeModified,
  canTransitionTo,
  onEdit,
  onAssign,
  onSubmitQC,
  onQCPass,
  onQCFail,
  onRevision,
  onDeliver,
  onCancel,
}) => {
  const [open, setOpen] = useState(false);
  const actions = [];

  if (canBeModified)
    actions.push({ label: "Edit Job", icon: Edit, onClick: onEdit });
  if (job.status === "PENDING" || job.status === "REJECTED")
    actions.push({
      label: "Assign Karigar",
      icon: UserPlus,
      onClick: onAssign,
      primary: true,
    });
  if (job.status === "IN_PROGRESS" && canTransitionTo("QUALITY_CHECK"))
    actions.push({
      label: "Submit for QC",
      icon: ClipboardCheck,
      onClick: onSubmitQC,
      primary: true,
    });
  if (job.status === "QUALITY_CHECK") {
    if (canTransitionTo("COMPLETED"))
      actions.push({
        label: "Approve & Complete",
        icon: CheckCircle,
        onClick: onQCPass,
        primary: true,
      });
    if (canTransitionTo("REVISION_REQUIRED"))
      actions.push({
        label: "Request Revision",
        icon: XCircle,
        onClick: onQCFail,
        variant: "outline",
      });
  }
  if (job.status === "REVISION_REQUIRED" && canTransitionTo("QUALITY_CHECK"))
    actions.push({
      label: "Revision Complete",
      icon: RefreshCw,
      onClick: onRevision,
      primary: true,
    });
  if (job.status === "COMPLETED" && canTransitionTo("DELIVERED"))
    actions.push({
      label: "Mark Delivered",
      icon: Truck,
      onClick: onDeliver,
      primary: true,
    });
  if (canTransitionTo("CANCELLED"))
    actions.push({
      label: "Cancel Job",
      icon: Ban,
      onClick: onCancel,
      variant: "destructive",
    });

  if (actions.length === 0) return null;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" size="icon" className="lg:hidden h-10 w-10">
          <MoreVertical className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="bottom" className="h-auto max-h-[80vh]">
        <SheetHeader className="pb-4">
          <SheetTitle>Actions</SheetTitle>
        </SheetHeader>
        <div className="grid gap-2 pb-6">
          {actions.map((action, idx) => (
            <Button
              key={idx}
              variant={
                action.variant || (action.primary ? "default" : "outline")
              }
              className="w-full justify-start h-12 text-base"
              onClick={() => {
                setOpen(false);
                action.onClick();
              }}
            >
              <action.icon className="h-5 w-5 mr-3" />
              {action.label}
            </Button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// ============================================================================
// KARIGAR SIDEBAR CARD
// ============================================================================

const KarigarCard = ({
  karigar,
  job,
  unreadCount,
  onNavigate,
  onMessagesClick,
  onAssign,
}) => {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
          <User className="h-4 w-4" />
          Assigned Karigar
        </CardTitle>
      </CardHeader>
      <CardContent>
        {karigar ? (
          <div className="space-y-3">
            <div
              className="cursor-pointer hover:bg-muted/50 p-2 rounded -mx-2 transition-colors"
              onClick={() =>
                onNavigate(`/karigar-management/karigars/${karigar._id}`)
              }
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-base sm:text-lg font-medium">
                    {karigar.name?.charAt(0)?.toUpperCase() || "K"}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-sm sm:text-base truncate">
                    {karigar.name}
                  </p>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    {karigar.karigarCode}
                  </p>
                </div>
              </div>
            </div>
            {karigar.phoneNumber && (
              <a
                href={`tel:${karigar.phoneNumber}`}
                className="flex items-center gap-2 text-sm p-2 -mx-2 rounded hover:bg-muted/50 transition-colors"
              >
                <Phone className="h-4 w-4 text-muted-foreground" />
                <span className="hover:underline">{karigar.phoneNumber}</span>
              </a>
            )}
            <Button
              variant="outline"
              size="sm"
              className="w-full h-10"
              onClick={onMessagesClick}
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Messages
              {unreadCount > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-xs">
                  {unreadCount}
                </Badge>
              )}
            </Button>
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
              <User className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground mb-3 text-sm">Not assigned</p>
            {(job.status === "PENDING" || job.status === "REJECTED") && (
              <Button size="sm" onClick={onAssign} className="h-10">
                <UserPlus className="h-4 w-4 mr-2" />
                Assign Karigar
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// TIMELINE CARD
// ============================================================================

const TimelineCard = ({ job, isOverdue }) => {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base sm:text-lg flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          Timeline
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 sm:space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Created</span>
          <span className="font-medium">
            {job.createdAt
              ? format(new Date(job.createdAt), "dd MMM yyyy")
              : "-"}
          </span>
        </div>
        {job.assignedAt && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Assigned</span>
            <span className="font-medium">
              {format(new Date(job.assignedAt), "dd MMM yyyy")}
            </span>
          </div>
        )}
        {job.startedAt && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Started</span>
            <span className="font-medium">
              {format(new Date(job.startedAt), "dd MMM yyyy")}
            </span>
          </div>
        )}
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Expected</span>
          <span className={cn("font-medium", isOverdue && "text-red-600")}>
            {job.expectedCompletionDate
              ? format(new Date(job.expectedCompletionDate), "dd MMM yyyy")
              : "-"}
          </span>
        </div>
        {job.completedAt && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Completed</span>
            <span className="font-medium text-green-600">
              {format(new Date(job.completedAt), "dd MMM yyyy")}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const JobDetailPage = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();

  const {
    currentJob,
    currentJobImages,
    currentJobMaterials,
    currentJobPayments,
    loading,
    error,
    successMessage,
    allowedTransitions = [],
    canBeModified = false,
    orderDetails,
    productDetails,
    materialPermissions,
    pricingPreview,
    getPricingPreview,
    getJobById,
    getImages,
    getMaterials,
    getJobPayments,
    uploadReferenceImages,
    deleteReferenceImage,
    uploadCompletedImages,
    deleteCompletedImage,
    uploadItemImages,
    passQualityCheck,
    failQualityCheck,
    submitForQualityCheck,
    completeRevision,
    markDelivered,
    cancel,
    sendMessage,
    issueMaterial,
    confirmMaterialReceived,
    recordMaterialReturn,
    updateIssuedMaterial,
    deleteIssuedMaterial,
    undoMaterialReceived,
    updateMaterialReturn,
    deleteMaterialReturn,
    setPricing,
    addDeduction,
    removeDeduction,
    clearError,
    clearSuccessMessage,
    clearCurrentJob,
  } = useJobManagement();

  const {
    jobPaymentCalculation,
    loading: paymentLoading,
    getPaymentsByJob,
    calculateJobPayment,
    addPayment,
    processPayment,
    cancelPayment,
  } = usePaymentManagement();

  const [activeTab, setActiveTab] = useState("details");
  const [isUploading, setIsUploading] = useState(false);
  const [showQCPassDialog, setShowQCPassDialog] = useState(false);
  const [showQCFailDialog, setShowQCFailDialog] = useState(false);
  const [showSubmitQCDialog, setShowSubmitQCDialog] = useState(false);
  const [showRevisionDialog, setShowRevisionDialog] = useState(false);
  const [showDeliverDialog, setShowDeliverDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  const [qualityRating, setQualityRating] = useState(4);
  const [qualityScore, setQualityScore] = useState(80);
  const [qcNotes, setQcNotes] = useState("");
  const [finalWeight, setFinalWeight] = useState("");
  const [finalPurity, setFinalPurity] = useState("");
  const [hallmarkNumber, setHallmarkNumber] = useState("");
  const [failReason, setFailReason] = useState("");
  const [revisionNotes, setRevisionNotes] = useState("");
  const [submitQCNotes, setSubmitQCNotes] = useState("");
  const [finishedWeight, setFinishedWeight] = useState("");
  const [deliverNotes, setDeliverNotes] = useState("");
  const [deliveredTo, setDeliveredTo] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [qcDialogImages, setQcDialogImages] = useState([]);
  const [messageAttachments, setMessageAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(() => {
    if (jobId) loadJobData();
    return () => clearCurrentJob();
  }, [jobId]);

  const loadJobData = () => {
    getJobById(jobId);
    getImages(jobId);
    getMaterials(jobId);
    getJobPayments(jobId);
    getPaymentsByJob(jobId);
  };

  useEffect(() => {
    const action = searchParams.get("action");
    if (!currentJob) return;
    if (action === "qc" && currentJob.status === "QUALITY_CHECK")
      setShowQCPassDialog(true);
    else if (action === "deliver" && currentJob.status === "COMPLETED")
      setShowDeliverDialog(true);
    else if (action === "materials") setActiveTab("materials");
    else if (action === "images") setActiveTab("images");
  }, [searchParams, currentJob]);

  useEffect(() => {
    if (currentJob?.completionDetails?.finalWeight)
      setFinalWeight(currentJob.completionDetails.finalWeight.toString());
  }, [currentJob]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      clearSuccessMessage();
      loadJobData();
    }
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [successMessage, error]);

  const handleUploadReferenceImages = async (files) => {
    setIsUploading(true);
    try {
      await uploadReferenceImages(jobId, files);
      await getImages(jobId);
    } finally {
      setIsUploading(false);
    }
  };
  const handleUploadCompletedImages = async (files) => {
    setIsUploading(true);
    try {
      await uploadCompletedImages(jobId, files);
      await getImages(jobId);
    } finally {
      setIsUploading(false);
    }
  };
  const handleUploadItemImages = async (files) => {
    setIsUploading(true);
    try {
      await uploadItemImages(jobId, files);
      await getImages(jobId);
    } finally {
      setIsUploading(false);
    }
  };
  const handleDeleteReferenceImage = async (imageUrl) => {
    try {
      await deleteReferenceImage(jobId, imageUrl);
      await getImages(jobId);
      toast.success("Image deleted");
    } catch {
      toast.error("Failed to delete image");
    }
  };
  const handleDeleteCompletedImage = async (imageUrl) => {
    try {
      await deleteCompletedImage(jobId, imageUrl);
      await getImages(jobId);
      toast.success("Image deleted");
    } catch {
      toast.error("Failed to delete image");
    }
  };

  const handlePassQC = async () => {
    setSubmitting(true);
    try {
      const ratingLabel = RATING_NUMBER_TO_LABEL[qualityRating] || "GOOD";
      await passQualityCheck(
        jobId,
        ratingLabel,
        qualityScore,
        qcNotes,
        finalWeight ? parseFloat(finalWeight) : undefined,
        finalPurity || undefined,
        hallmarkNumber || undefined,
        qcDialogImages
      );
      setShowQCPassDialog(false);
      resetQCForm();
    } finally {
      setSubmitting(false);
    }
  };

  const handleFailQC = async () => {
    if (!failReason.trim()) {
      toast.error("Please provide a reason");
      return;
    }
    setSubmitting(true);
    try {
      await failQualityCheck(jobId, failReason, qcNotes, qcDialogImages);
      setShowQCFailDialog(false);
      resetQCForm();
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitForQC = async () => {
    setSubmitting(true);
    try {
      await submitForQualityCheck(
        jobId,
        submitQCNotes,
        qcDialogImages,
        finishedWeight ? parseFloat(finishedWeight) : undefined
      );
      setShowSubmitQCDialog(false);
      setSubmitQCNotes("");
      setFinishedWeight("");
      setQcDialogImages([]);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompleteRevision = async () => {
    setSubmitting(true);
    try {
      await completeRevision(jobId, revisionNotes, qcDialogImages);
      setShowRevisionDialog(false);
      setRevisionNotes("");
      setQcDialogImages([]);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkDelivered = async () => {
    setSubmitting(true);
    try {
      await markDelivered(jobId, deliverNotes, deliveredTo || undefined);
      setShowDeliverDialog(false);
      setDeliverNotes("");
      setDeliveredTo("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelJob = async () => {
    if (!cancelReason.trim()) {
      toast.error("Please provide a reason");
      return;
    }
    setSubmitting(true);
    try {
      await cancel(jobId, cancelReason);
      setShowCancelDialog(false);
      setCancelReason("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      await sendMessage(jobId, newMessage.trim(), messageAttachments);
      setNewMessage("");
      setMessageAttachments([]);
      loadJobData();
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const handleIssueMaterial = (jId, data) => issueMaterial(jId, data);
  const handleConfirmReceived = (jId, mId) => confirmMaterialReceived(jId, mId);
  const handleRecordReturn = (jId, data) => recordMaterialReturn(jId, data);
  const handleUpdateMaterial = (jId, mId, data, forceUpdate) =>
    updateIssuedMaterial(jId, mId, data, forceUpdate);
  const handleDeleteMaterial = (jId, mId, reason, forceDelete) =>
    deleteIssuedMaterial(jId, mId, reason, forceDelete);
  const handleUndoReceived = (jId, mId, reason) =>
    undoMaterialReceived(jId, mId, reason);
  const handleUpdateReturn = (jId, data, adjustmentReason) =>
    updateMaterialReturn(jId, data, adjustmentReason);
  const handleDeleteReturn = (jId, reason) => deleteMaterialReturn(jId, reason);
  const handleUpdatePricing = (jId, data) => setPricing(jId, data);
  const handleAddDeduction = (jId, data) => addDeduction(jId, data);
  const handleRemoveDeduction = (jId, deductionIndex) =>
    removeDeduction(jId, deductionIndex);
  const handleCalculatePayment = (jId) => calculateJobPayment(jId);
  const handleCreatePayment = (data) => addPayment(data);
  const handleProcessPayment = (pId, data) =>
    processPayment(
      pId,
      data.paymentMethod,
      data.transactionId,
      data.referenceNumber,
      data.notes
    );
  const handleCancelPayment = (pId, reason) => cancelPayment(pId, reason);
  const handleFetchPricingPreview = (jId) => getPricingPreview(jId);

  const resetQCForm = () => {
    setQualityRating(4);
    setQualityScore(80);
    setQcNotes("");
    setFinalWeight("");
    setFinalPurity("");
    setHallmarkNumber("");
    setFailReason("");
    setQcDialogImages([]);
  };

  const getKarigar = () => {
    if (!currentJob) return null;
    if (currentJob.karigarId && typeof currentJob.karigarId === "object")
      return { ...currentJob.karigarId, _id: currentJob.karigarId._id };
    if (currentJob.karigarName)
      return {
        _id: currentJob.karigarId,
        name: currentJob.karigarName,
        karigarCode: currentJob.karigarCode,
        phoneNumber: currentJob.karigarPhone,
      };
    return null;
  };

  const getQCData = () => {
    if (!currentJob?.completionDetails) return null;
    const cd = currentJob.completionDetails;
    if (!cd.inspectedAt && !cd.qualityRating && !cd.qualityScore) return null;
    return {
      passed: ["EXCELLENT", "GOOD", "SATISFACTORY"].includes(cd.qualityRating),
      rating: QUALITY_RATING_STARS[cd.qualityRating] || 0,
      ratingLabel: cd.qualityRating,
      score: cd.qualityScore,
      notes: cd.qualityNotes,
      finalWeight: cd.finalWeight,
      finalPurity: cd.finalPurity,
      hallmarkNumber: cd.hallmarkNumber,
      inspectedBy: cd.inspectedByName,
      inspectedAt: cd.inspectedAt,
      revisions: cd.revisions || [],
    };
  };

  const isOverdue = () => {
    if (!currentJob?.expectedCompletionDate) return false;
    if (currentJob.isOverdue) return true;
    if (["COMPLETED", "DELIVERED", "CANCELLED"].includes(currentJob.status))
      return false;
    return new Date(currentJob.expectedCompletionDate) < new Date();
  };

  const getProgressPercentage = () => {
    if (!currentJob) return 0;
    const map = {
      PENDING: 0,
      ASSIGNED: 10,
      ACCEPTED: 20,
      IN_PROGRESS: 50,
      ON_HOLD: 50,
      QUALITY_CHECK: 80,
      REVISION_REQUIRED: 60,
      COMPLETED: 95,
      DELIVERED: 100,
    };
    return map[currentJob.status] || 0;
  };

  const canTransitionTo = (status) => {
    if (!currentJob) return false;
    if (allowedTransitions?.length > 0)
      return allowedTransitions.includes(status);
    const fallback = {
      PENDING: ["ASSIGNED", "CANCELLED"],
      ASSIGNED: ["ACCEPTED", "REJECTED", "CANCELLED"],
      ACCEPTED: ["IN_PROGRESS", "ON_HOLD", "CANCELLED"],
      IN_PROGRESS: ["QUALITY_CHECK", "ON_HOLD", "CANCELLED"],
      ON_HOLD: ["IN_PROGRESS", "ACCEPTED", "CANCELLED"],
      QUALITY_CHECK: ["COMPLETED", "REVISION_REQUIRED"],
      REVISION_REQUIRED: ["QUALITY_CHECK", "IN_PROGRESS", "CANCELLED"],
      COMPLETED: ["DELIVERED"],
      DELIVERED: [],
      CANCELLED: [],
    };
    return (fallback[currentJob.status] || []).includes(status);
  };

  const getUnreadMessageCount = () =>
    currentJob?.messages?.filter((m) => !m.isRead && m.sender === "KARIGAR")
      .length || 0;
  const getTotalImageCount = () => {
    const imgs = currentJobImages || {};
    return (
      (imgs.referenceImages?.length || 0) +
      (imgs.itemImages?.length || 0) +
      (imgs.completedImages?.length || 0) +
      (imgs.qcImages?.length || 0)
    );
  };
  const canUploadCompletedImages = () =>
    ["IN_PROGRESS", "QUALITY_CHECK", "REVISION_REQUIRED"].includes(
      currentJob?.status
    );

  if (loading.detail)
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <LoadingSpinner />
      </div>
    );

  if (!currentJob)
    return (
      <div className="container mx-auto py-8 px-4 text-center">
        <div className="max-w-md mx-auto">
          <Package className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">Job not found</h2>
          <p className="text-muted-foreground mb-6">
            The job you're looking for doesn't exist or has been removed.
          </p>
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>
        </div>
      </div>
    );

  const job = currentJob;
  const karigar = getKarigar();
  const qcData = getQCData();
  const progressPercentage = getProgressPercentage();
  const unreadCount = getUnreadMessageCount();
  const totalImages = getTotalImageCount();
  const images = currentJobImages || {};
  const overdueStatus = isOverdue();
  const pendingPaymentsCount =
    currentJobPayments?.payments?.pending?.length || 0;

  return (
    <div className="min-h-screen bg-background">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        <div className="container mx-auto px-3 sm:px-4 lg:px-6">
          <div className="flex items-center justify-between py-3 sm:py-4 gap-2 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="shrink-0 h-9 w-9 sm:h-10 sm:w-10"
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-base sm:text-lg lg:text-2xl font-bold truncate">
                    {job.jobNumber}
                  </h1>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge className={cn("text-xs", STATUS_COLORS[job.status])}>
                      {job.status?.replace(/_/g, " ")}
                    </Badge>
                    {job.isUrgent && (
                      <Badge className="bg-red-500 text-white text-xs">
                        URGENT
                      </Badge>
                    )}
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-muted-foreground truncate mt-0.5">
                  {job.title}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="icon"
                className="lg:hidden h-9 w-9 sm:h-10 sm:w-10"
                onClick={() => setShowMobileSidebar(true)}
              >
                <Info className="h-4 w-4 sm:h-5 sm:w-5" />
              </Button>
              <MobileActionsSheet
                job={job}
                canBeModified={canBeModified}
                canTransitionTo={canTransitionTo}
                onEdit={() =>
                  navigate(`/karigar-management/jobs/${jobId}/edit`)
                }
                onAssign={() =>
                  navigate(`/karigar-management/jobs/${jobId}/assign`)
                }
                onSubmitQC={() => setShowSubmitQCDialog(true)}
                onQCPass={() => setShowQCPassDialog(true)}
                onQCFail={() => setShowQCFailDialog(true)}
                onRevision={() => setShowRevisionDialog(true)}
                onDeliver={() => setShowDeliverDialog(true)}
                onCancel={() => setShowCancelDialog(true)}
              />

              {/* Desktop Actions */}
              <div className="hidden lg:flex gap-2">
                {canBeModified && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate(`/karigar-management/jobs/${jobId}/edit`)
                    }
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                )}
                {(job.status === "PENDING" || job.status === "REJECTED") && (
                  <Button
                    size="sm"
                    onClick={() =>
                      navigate(`/karigar-management/jobs/${jobId}/assign`)
                    }
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Assign
                  </Button>
                )}
                {job.status === "IN_PROGRESS" &&
                  canTransitionTo("QUALITY_CHECK") && (
                    <Button
                      size="sm"
                      onClick={() => setShowSubmitQCDialog(true)}
                    >
                      <ClipboardCheck className="h-4 w-4 mr-2" />
                      Submit for QC
                    </Button>
                  )}
                {job.status === "QUALITY_CHECK" && (
                  <>
                    {canTransitionTo("REVISION_REQUIRED") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowQCFailDialog(true)}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Revision
                      </Button>
                    )}
                    {canTransitionTo("COMPLETED") && (
                      <Button
                        size="sm"
                        onClick={() => setShowQCPassDialog(true)}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                    )}
                  </>
                )}
                {job.status === "REVISION_REQUIRED" &&
                  canTransitionTo("QUALITY_CHECK") && (
                    <Button
                      size="sm"
                      onClick={() => setShowRevisionDialog(true)}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Revision Complete
                    </Button>
                  )}
                {job.status === "COMPLETED" && canTransitionTo("DELIVERED") && (
                  <Button size="sm" onClick={() => setShowDeliverDialog(true)}>
                    <Truck className="h-4 w-4 mr-2" />
                    Deliver
                  </Button>
                )}
                {canTransitionTo("CANCELLED") && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowCancelDialog(true)}
                  >
                    <Ban className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Sheet */}
      <Sheet open={showMobileSidebar} onOpenChange={setShowMobileSidebar}>
        <SheetContent side="right" className="w-[85vw] sm:w-[400px] p-0">
          <SheetHeader className="p-4 border-b">
            <SheetTitle>Job Details</SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-60px)]">
            <div className="p-4 space-y-4">
              <KarigarCard
                karigar={karigar}
                job={job}
                unreadCount={unreadCount}
                onNavigate={navigate}
                onMessagesClick={() => {
                  setShowMobileSidebar(false);
                  setActiveTab("messages");
                }}
                onAssign={() => {
                  setShowMobileSidebar(false);
                  navigate(`/karigar-management/jobs/${jobId}/assign`);
                }}
              />
              <TimelineCard job={job} isOverdue={overdueStatus} />
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">
        {overdueStatus && (
          <Card className="border-red-200 bg-red-50 mb-4 sm:mb-6">
            <CardContent className="p-3 sm:p-4 flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
              <div className="min-w-0">
                <span className="text-red-600 font-medium text-sm sm:text-base">
                  Overdue!
                </span>
                <span className="text-red-600 text-sm ml-2">
                  Expected:{" "}
                  {format(new Date(job.expectedCompletionDate), "dd MMM yyyy")}
                  {job.overdueBy > 0 &&
                    ` (${Math.round(job.overdueBy / 24)} days)`}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {progressPercentage > 0 && job.status !== "CANCELLED" && (
          <Card className="mb-4 sm:mb-6">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-sm sm:text-base">
                  Progress
                </span>
                <span className="text-xs sm:text-sm text-muted-foreground">
                  {progressPercentage}%
                </span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <div className="relative -mx-3 sm:mx-0 px-3 sm:px-0">
                <TabsList className="w-full h-auto p-1 flex overflow-x-auto scrollbar-hide gap-1 justify-start sm:justify-center">
                  <TabsTrigger
                    value="details"
                    className="shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5 sm:py-2"
                  >
                    Details
                  </TabsTrigger>
                  <TabsTrigger
                    value="images"
                    className="shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5 sm:py-2"
                  >
                    Images
                    {totalImages > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-1 text-xs h-5 px-1.5"
                      >
                        {totalImages}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger
                    value="materials"
                    className="shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5 sm:py-2"
                  >
                    Materials
                  </TabsTrigger>
                  <TabsTrigger
                    value="quality"
                    className="shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5 sm:py-2"
                  >
                    QC
                  </TabsTrigger>
                  <TabsTrigger
                    value="payments"
                    className="shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5 sm:py-2"
                  >
                    Payments
                    {pendingPaymentsCount > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center h-4 w-4 text-xs font-medium bg-yellow-500 text-white rounded-full">
                        {pendingPaymentsCount}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger
                    value="messages"
                    className="shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5 sm:py-2"
                  >
                    Messages
                    {unreadCount > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center h-4 w-4 text-xs font-medium bg-red-500 text-white rounded-full">
                        {unreadCount}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger
                    value="timeline"
                    className="shrink-0 text-xs sm:text-sm px-2.5 sm:px-3 py-1.5 sm:py-2"
                  >
                    History
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="details" className="space-y-4 mt-4">
                <CollapsibleSection
                  title="Job Information"
                  icon={Package}
                  defaultOpen={true}
                >
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Title
                      </p>
                      <p className="font-medium text-sm sm:text-base">
                        {job.title || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Job Type
                      </p>
                      <p className="font-medium text-sm sm:text-base">
                        {job.jobType?.replace(/_/g, " ") || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Work Type
                      </p>
                      <p className="font-medium text-sm sm:text-base">
                        {job.workType?.replace(/_/g, " ") || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Priority
                      </p>
                      <Badge
                        className={cn("text-xs", PRIORITY_COLORS[job.priority])}
                      >
                        {job.priority}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Created By
                      </p>
                      <p className="font-medium text-sm sm:text-base">
                        {job.createdByName || "-"}
                      </p>
                    </div>
                  </div>
                  {job.specifications &&
                    Object.keys(job.specifications).length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                          Specifications
                        </p>
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                          {(job.specifications.metalType ||
                            job.specifications.metalPurity) && (
                            <div>
                              <span className="text-muted-foreground text-xs">
                                Metal:
                              </span>{" "}
                              <span className="font-medium text-xs sm:text-sm">
                                {job.specifications.metalType ||
                                  job.specifications.metalPurity ||
                                  "-"}
                              </span>
                            </div>
                          )}
                          {job.specifications.targetWeight !== undefined && (
                            <div>
                              <span className="text-muted-foreground text-xs">
                                Target Weight:
                              </span>{" "}
                              <span className="font-medium text-xs sm:text-sm">
                                {job.specifications.targetWeight}g
                              </span>
                            </div>
                          )}
                          {job.specifications.hallmarkRequired !==
                            undefined && (
                            <div>
                              <span className="text-muted-foreground text-xs">
                                Hallmark:
                              </span>{" "}
                              <span className="font-medium text-xs sm:text-sm">
                                {job.specifications.hallmarkRequired
                                  ? "Required"
                                  : "Not Required"}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  {job.specialInstructions && (
                    <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-xs sm:text-sm font-medium text-yellow-800">
                        Special Instructions
                      </p>
                      <p className="text-xs sm:text-sm text-yellow-700 mt-1">
                        {job.specialInstructions}
                      </p>
                    </div>
                  )}
                </CollapsibleSection>

                {job.isFromOrder && orderDetails && (
                  <CollapsibleSection
                    title="Order Information"
                    icon={ShoppingCart}
                    defaultOpen={false}
                  >
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          Order Number
                        </p>
                        <p className="font-medium text-sm sm:text-base">
                          {orderDetails.orderNumber || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          Order Date
                        </p>
                        <p className="font-medium text-sm sm:text-base">
                          {orderDetails.orderDate
                            ? format(
                                new Date(orderDetails.orderDate),
                                "dd MMM yyyy"
                              )
                            : "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          Status
                        </p>
                        <Badge variant="outline" className="text-xs">
                          {orderDetails.orderStatus?.replace(/_/g, " ") || "-"}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          Customer
                        </p>
                        <p className="font-medium text-sm sm:text-base">
                          {orderDetails.customer?.name || "Guest"}
                        </p>
                      </div>
                    </div>
                  </CollapsibleSection>
                )}

                {productDetails && (
                  <CollapsibleSection
                    title="Product Information"
                    icon={FileText}
                    defaultOpen={false}
                  >
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          Product Name
                        </p>
                        <p className="font-medium text-sm sm:text-base">
                          {productDetails.productName || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                          SKU
                        </p>
                        <p className="font-medium text-sm sm:text-base">
                          {productDetails.productSKU || "-"}
                        </p>
                      </div>
                      {productDetails.grossWeight && (
                        <div>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            Gross Weight
                          </p>
                          <p className="font-medium text-sm sm:text-base">
                            {productDetails.grossWeight}g
                          </p>
                        </div>
                      )}
                    </div>
                  </CollapsibleSection>
                )}

                {job.karigarNotes && (
                  <CollapsibleSection
                    title="Karigar Work Notes"
                    icon={Wrench}
                    defaultOpen={true}
                  >
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <pre className="text-xs sm:text-sm whitespace-pre-wrap font-sans text-blue-900">
                        {job.karigarNotes}
                      </pre>
                    </div>
                  </CollapsibleSection>
                )}
              </TabsContent>

              <TabsContent value="images" className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ImageGallery
                    title="Reference Images"
                    description="Design references"
                    images={images.referenceImages || []}
                    onUpload={handleUploadReferenceImages}
                    onDelete={handleDeleteReferenceImage}
                    isUploading={isUploading}
                    maxFiles={10}
                  />
                  {(job.jobType === "REPAIR" ||
                    job.jobType === "ALTERATION") && (
                    <ImageGallery
                      title="Item Images"
                      description="Original item photos"
                      images={images.itemImages || []}
                      onUpload={handleUploadItemImages}
                      onDelete={null}
                      canDelete={false}
                      isUploading={isUploading}
                      maxFiles={5}
                    />
                  )}
                  <ImageGallery
                    title="Completed Work"
                    description="Finished work photos"
                    images={images.completedImages || []}
                    onUpload={handleUploadCompletedImages}
                    onDelete={handleDeleteCompletedImage}
                    canUpload={canUploadCompletedImages()}
                    isUploading={isUploading}
                    maxFiles={10}
                  />
                  {images.qcImages?.length > 0 && (
                    <ImageGallery
                      title="QC Images"
                      images={images.qcImages}
                      canUpload={false}
                      canDelete={false}
                    />
                  )}
                </div>
              </TabsContent>

              <TabsContent value="materials" className="mt-4">
                <JobMaterialsTab
                  job={currentJob}
                  currentJobMaterials={currentJobMaterials}
                  loading={loading}
                  onIssueMaterial={handleIssueMaterial}
                  onConfirmReceived={handleConfirmReceived}
                  onRecordReturn={handleRecordReturn}
                  onUpdateMaterial={handleUpdateMaterial}
                  onDeleteMaterial={handleDeleteMaterial}
                  onUndoReceived={handleUndoReceived}
                  onUpdateReturn={handleUpdateReturn}
                  onDeleteReturn={handleDeleteReturn}
                  materialPermissions={materialPermissions}
                  onRefresh={loadJobData}
                />
              </TabsContent>

              <TabsContent value="quality" className="mt-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                      <ClipboardCheck className="h-4 w-4" />
                      Quality Check
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {job.status === "QUALITY_CHECK" && !qcData && (
                      <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="h-5 w-5 text-orange-600" />
                          <h4 className="font-medium text-orange-800 text-sm sm:text-base">
                            Awaiting Quality Check
                          </h4>
                        </div>
                        {job.completionDetails?.finalWeight && (
                          <p className="text-xs sm:text-sm text-orange-700 mt-2">
                            <strong>Submitted Weight:</strong>{" "}
                            {job.completionDetails.finalWeight}g
                          </p>
                        )}
                        <div className="mt-4 flex flex-col sm:flex-row gap-2">
                          <Button
                            size="sm"
                            onClick={() => setShowQCPassDialog(true)}
                            className="w-full sm:w-auto"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowQCFailDialog(true)}
                            className="w-full sm:w-auto"
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Request Revision
                          </Button>
                        </div>
                      </div>
                    )}
                    {qcData && (
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <h4 className="font-medium mb-3 text-sm sm:text-base">
                          QC Result
                        </h4>
                        <div className="grid grid-cols-2 gap-3 sm:gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground text-xs sm:text-sm">
                              Result:
                            </span>{" "}
                            <Badge
                              className={cn(
                                "text-xs",
                                qcData.passed
                                  ? "bg-green-100 text-green-800"
                                  : "bg-red-100 text-red-800"
                              )}
                            >
                              {qcData.passed ? "Passed" : "Failed"}
                            </Badge>
                          </div>
                          {qcData.ratingLabel && (
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-muted-foreground text-xs sm:text-sm">
                                Rating:
                              </span>
                              <div className="flex">
                                {[1, 2, 3, 4, 5].map((s) => (
                                  <Star
                                    key={s}
                                    className={cn(
                                      "h-3 w-3 sm:h-4 sm:w-4",
                                      s <= qcData.rating
                                        ? "text-yellow-500 fill-yellow-500"
                                        : "text-gray-300"
                                    )}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                          {qcData.finalWeight && (
                            <div>
                              <span className="text-muted-foreground text-xs sm:text-sm">
                                Final Weight:
                              </span>{" "}
                              <span className="font-medium">
                                {qcData.finalWeight}g
                              </span>
                            </div>
                          )}
                        </div>
                        {qcData.notes && (
                          <div className="mt-3">
                            <span className="text-muted-foreground text-xs sm:text-sm">
                              Notes:
                            </span>
                            <p className="mt-1 text-xs sm:text-sm bg-white p-2 rounded border">
                              {qcData.notes}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    {!qcData && job.status !== "QUALITY_CHECK" && (
                      <div className="text-center py-8">
                        <ClipboardCheck className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                        <p className="text-muted-foreground text-sm">
                          No QC performed yet
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="payments" className="mt-4">
                <JobPaymentsTab
                  job={job}
                  jobPaymentData={currentJobPayments}
                  jobPaymentCalculation={jobPaymentCalculation}
                  pricingPreview={pricingPreview}
                  loading={{
                    ...loading,
                    ...paymentLoading,
                    pricingPreview: loading.pricingPreview,
                  }}
                  onUpdatePricing={handleUpdatePricing}
                  onAddDeduction={handleAddDeduction}
                  onRemoveDeduction={handleRemoveDeduction}
                  onCalculatePayment={handleCalculatePayment}
                  onFetchPricingPreview={handleFetchPricingPreview}
                  onCreatePayment={handleCreatePayment}
                  onProcessPayment={handleProcessPayment}
                  onCancelPayment={handleCancelPayment}
                  onRefresh={loadJobData}
                />
              </TabsContent>

              <TabsContent value="messages" className="mt-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Messages
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <ScrollArea className="h-[300px] sm:h-[400px] pr-4">
                      <div className="space-y-3">
                        {job.messages?.length > 0 ? (
                          job.messages.map((msg, idx) => (
                            <div
                              key={msg._id || idx}
                              className={cn(
                                "p-3 rounded-lg",
                                msg.sender === "KARIGAR"
                                  ? "bg-blue-50 mr-4 sm:mr-8"
                                  : "bg-gray-100 ml-4 sm:ml-8"
                              )}
                            >
                              <div className="flex flex-wrap justify-between items-start gap-2 mb-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-xs sm:text-sm">
                                    {msg.senderName}
                                  </span>
                                  <Badge
                                    variant="outline"
                                    className="text-xs h-5"
                                  >
                                    {msg.sender}
                                  </Badge>
                                  {!msg.isRead && msg.sender === "KARIGAR" && (
                                    <Badge className="bg-red-500 text-white text-xs h-5">
                                      New
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {format(
                                    new Date(msg.timestamp),
                                    "dd MMM, hh:mm a"
                                  )}
                                </span>
                              </div>
                              <p className="text-xs sm:text-sm">
                                {msg.message}
                              </p>
                              {msg.attachments?.length > 0 && (
                                <div className="flex gap-2 mt-2 flex-wrap">
                                  {msg.attachments.map((a, i) => (
                                    <a
                                      key={i}
                                      href={a.url || a}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-blue-600 underline"
                                    >
                                      Attachment {i + 1}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8">
                            <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                            <p className="text-muted-foreground text-sm">
                              No messages yet
                            </p>
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                    {karigar &&
                      !["CANCELLED", "DELIVERED"].includes(job.status) && (
                        <div className="pt-4 border-t space-y-3">
                          {messageAttachments.length > 0 && (
                            <div className="flex gap-2 flex-wrap">
                              {messageAttachments.map((f, i) => (
                                <div
                                  key={i}
                                  className="relative group h-12 w-12 sm:h-16 sm:w-16 rounded border overflow-hidden"
                                >
                                  <img
                                    src={URL.createObjectURL(f)}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                  <button
                                    type="button"
                                    className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl"
                                    onClick={() =>
                                      setMessageAttachments(
                                        messageAttachments.filter(
                                          (_, j) => j !== i
                                        )
                                      )
                                    }
                                  >
                                    <X className="h-3 w-3" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Textarea
                              placeholder="Type your message..."
                              value={newMessage}
                              onChange={(e) => setNewMessage(e.target.value)}
                              rows={2}
                              className="flex-1 text-sm min-h-[44px]"
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                  e.preventDefault();
                                  handleSendMessage();
                                }
                              }}
                            />
                            <div className="flex flex-col gap-1">
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => {
                                    setMessageAttachments([
                                      ...messageAttachments,
                                      ...Array.from(e.target.files || []),
                                    ]);
                                    e.target.value = "";
                                  }}
                                />
                                <div className="h-9 w-9 sm:h-10 sm:w-10 border rounded flex items-center justify-center hover:bg-muted transition-colors">
                                  <Camera className="h-4 w-4 text-muted-foreground" />
                                </div>
                              </label>
                              <Button
                                onClick={handleSendMessage}
                                disabled={!newMessage.trim() || sendingMessage}
                                size="icon"
                                className="h-9 w-9 sm:h-10 sm:w-10"
                              >
                                {sendingMessage ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Send className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="timeline" className="mt-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Job History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {job.timeline?.length > 0 ? (
                      <div className="space-y-0">
                        {[...job.timeline].reverse().map((e, i) => (
                          <div key={e._id || i} className="flex gap-3 sm:gap-4">
                            <div className="flex flex-col items-center">
                              <div
                                className={cn(
                                  "h-7 w-7 sm:h-8 sm:w-8 rounded-full flex items-center justify-center shrink-0",
                                  STATUS_COLORS[e.status]?.split(" ")[0] ||
                                    "bg-primary/10"
                                )}
                              >
                                <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                              </div>
                              {i < job.timeline.length - 1 && (
                                <div className="w-0.5 flex-1 bg-muted min-h-[24px]" />
                              )}
                            </div>
                            <div className="flex-1 pb-4 min-w-0">
                              <p className="font-medium text-sm sm:text-base">
                                {e.status?.replace(/_/g, " ")}
                              </p>
                              <p className="text-xs sm:text-sm text-muted-foreground">
                                {e.timestamp
                                  ? format(
                                      new Date(e.timestamp),
                                      "dd MMM yyyy, hh:mm a"
                                    )
                                  : "-"}
                              </p>
                              {e.updatedByName && (
                                <p className="text-xs text-muted-foreground">
                                  By: {e.updatedByName}
                                </p>
                              )}
                              {e.notes && (
                                <p className="text-xs sm:text-sm mt-1 bg-muted/50 p-2 rounded">
                                  {e.notes}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <Clock className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
                        <p className="text-muted-foreground text-sm">
                          No timeline data
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Desktop Sidebar */}
          <div className="hidden lg:block space-y-6">
            <KarigarCard
              karigar={karigar}
              job={job}
              unreadCount={unreadCount}
              onNavigate={navigate}
              onMessagesClick={() => setActiveTab("messages")}
              onAssign={() =>
                navigate(`/karigar-management/jobs/${jobId}/assign`)
              }
            />
            <TimelineCard job={job} isOverdue={overdueStatus} />
          </div>
        </div>
      </div>

      {/* Dialogs */}
      <AlertDialog open={showQCPassDialog} onOpenChange={setShowQCPassDialog}>
        <AlertDialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg sm:text-xl">
              Approve Quality Check
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm">Quality Rating</Label>
              <div className="flex items-center gap-1 sm:gap-2 mt-2 flex-wrap">
                {[1, 2, 3, 4, 5].map((r) => (
                  <Button
                    key={r}
                    type="button"
                    variant={qualityRating >= r ? "default" : "outline"}
                    size="icon"
                    className="h-9 w-9 sm:h-10 sm:w-10"
                    onClick={() => setQualityRating(r)}
                  >
                    <Star
                      className={cn(
                        "h-4 w-4",
                        qualityRating >= r && "fill-current"
                      )}
                    />
                  </Button>
                ))}
                <span className="text-xs sm:text-sm text-muted-foreground ml-2">
                  {RATING_NUMBER_TO_LABEL[qualityRating]}
                </span>
              </div>
            </div>
            <div>
              <Label className="text-sm">Score (0-100)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={qualityScore}
                onChange={(e) => setQualityScore(parseInt(e.target.value) || 0)}
                className="h-10 sm:h-11"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div>
                <Label className="text-sm">Final Weight (g)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={finalWeight}
                  onChange={(e) => setFinalWeight(e.target.value)}
                  className="h-10 sm:h-11"
                />
              </div>
              <div>
                <Label className="text-sm">Purity</Label>
                <Input
                  value={finalPurity}
                  onChange={(e) => setFinalPurity(e.target.value)}
                  placeholder="22K"
                  className="h-10 sm:h-11"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm">Hallmark Number</Label>
              <Input
                value={hallmarkNumber}
                onChange={(e) => setHallmarkNumber(e.target.value)}
                className="h-10 sm:h-11"
              />
            </div>
            <div>
              <Label className="text-sm">Notes</Label>
              <Textarea
                value={qcNotes}
                onChange={(e) => setQcNotes(e.target.value)}
                rows={3}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-sm">QC Images</Label>
              <ImageUpload
                files={qcDialogImages}
                onFilesChange={setQcDialogImages}
                maxFiles={5}
                compact
                gridCols={4}
                allowCamera
              />
            </div>
          </div>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel
              disabled={submitting}
              className="w-full sm:w-auto"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePassQC}
              disabled={submitting}
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
            >
              {submitting ? "Processing..." : "Approve"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showQCFailDialog} onOpenChange={setShowQCFailDialog}>
        <AlertDialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg sm:text-xl">
              Request Revision
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-sm">Reason *</Label>
              <Textarea
                value={failReason}
                onChange={(e) => setFailReason(e.target.value)}
                rows={4}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-sm">QC Images</Label>
              <ImageUpload
                files={qcDialogImages}
                onFilesChange={setQcDialogImages}
                maxFiles={5}
                compact
                gridCols={4}
                allowCamera
              />
            </div>
          </div>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel
              disabled={submitting}
              className="w-full sm:w-auto"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleFailQC}
              disabled={!failReason.trim() || submitting}
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
            >
              {submitting ? "Processing..." : "Request Revision"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showSubmitQCDialog}
        onOpenChange={setShowSubmitQCDialog}
      >
        <AlertDialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg sm:text-xl">
              Submit for Quality Check
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-sm">Finished Weight (g)</Label>
              <Input
                type="number"
                step="0.01"
                value={finishedWeight}
                onChange={(e) => setFinishedWeight(e.target.value)}
                className="h-10 sm:h-11"
              />
            </div>
            <div>
              <Label className="text-sm">Notes</Label>
              <Textarea
                value={submitQCNotes}
                onChange={(e) => setSubmitQCNotes(e.target.value)}
                rows={3}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-sm">Work Images</Label>
              <ImageUpload
                files={qcDialogImages}
                onFilesChange={setQcDialogImages}
                maxFiles={5}
                compact
                gridCols={4}
                allowCamera
              />
            </div>
          </div>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel
              disabled={submitting}
              className="w-full sm:w-auto"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmitForQC}
              disabled={submitting}
              className="w-full sm:w-auto"
            >
              {submitting ? "Submitting..." : "Submit for QC"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={showRevisionDialog}
        onOpenChange={setShowRevisionDialog}
      >
        <AlertDialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg sm:text-xl">
              Complete Revision
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-sm">Notes</Label>
              <Textarea
                value={revisionNotes}
                onChange={(e) => setRevisionNotes(e.target.value)}
                rows={4}
                className="text-sm"
              />
            </div>
            <div>
              <Label className="text-sm">Updated Work Images</Label>
              <ImageUpload
                files={qcDialogImages}
                onFilesChange={setQcDialogImages}
                maxFiles={5}
                compact
                gridCols={4}
                allowCamera
              />
            </div>
          </div>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel
              disabled={submitting}
              className="w-full sm:w-auto"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCompleteRevision}
              disabled={submitting}
              className="w-full sm:w-auto"
            >
              {submitting ? "Processing..." : "Complete Revision"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeliverDialog} onOpenChange={setShowDeliverDialog}>
        <AlertDialogContent className="max-w-[95vw] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg sm:text-xl">
              Mark as Delivered
            </AlertDialogTitle>
          </AlertDialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-sm">Delivered To</Label>
              <Input
                value={deliveredTo}
                onChange={(e) => setDeliveredTo(e.target.value)}
                placeholder="Enter receiver name"
                className="h-10 sm:h-11"
              />
            </div>
            <div>
              <Label className="text-sm">Notes</Label>
              <Textarea
                value={deliverNotes}
                onChange={(e) => setDeliverNotes(e.target.value)}
                rows={3}
                className="text-sm"
              />
            </div>
          </div>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel
              disabled={submitting}
              className="w-full sm:w-auto"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleMarkDelivered}
              disabled={submitting}
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? "Processing..." : "Mark Delivered"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent className="max-w-[95vw] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg sm:text-xl">
              Cancel Job
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The job will be permanently
              cancelled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label className="text-sm">Reason *</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Please provide a reason for cancellation"
              className="text-sm"
            />
          </div>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel
              disabled={submitting}
              className="w-full sm:w-auto"
            >
              Keep Job
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelJob}
              disabled={!cancelReason.trim() || submitting}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
            >
              {submitting ? "Cancelling..." : "Cancel Job"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default JobDetailPage;
